"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AzureVoicesHelper = void 0;
const audio_analysis_helper_1 = require("./audio-analysis.helper");
class AzureVoicesHelper {
    /**
     * Load available Azure voices from the API
     */
    static async loadAzureVoices(component) {
        component.isLoading = true;
        try {
            // Load Azure voices via API
            const response = await component.api.back.get('audio.voices');
            if (response.success && response.voices) {
                component.availableVoices = response.voices;
                // Set default voice if none selected
                if (!component.selectedVoice && component.availableVoices.length > 0) {
                    // Try to find a voice that matches the current language
                    const languageCode = component.language || 'en-US';
                    const matchingVoice = component.availableVoices.find(voice => voice.locale && voice.locale.startsWith(languageCode.split('-')[0]));
                    component.selectedVoice = matchingVoice || component.availableVoices[0];
                    component.voice = component.selectedVoice.name;
                }
            }
            else {
                console.error('Failed to load Azure voices:', response.error);
                component.errorMessage = 'Failed to load voices';
            }
        }
        catch (error) {
            console.error('Failed to load Azure voices:', error);
            component.errorMessage = 'Failed to load voices';
        }
        finally {
            component.isLoading = false;
        }
    }
    /**
     * Change the selected Azure voice
     */
    static async changeAzureVoice(component, voice) {
        component.selectedVoice = voice;
        component.voice = voice.name;
    }
    /**
     * Synthesize speech using Azure TTS
     */
    static async synthesizeAzureSpeech(component, text) {
        if (!text.trim() || !component.selectedVoice)
            return;
        component.isSpeaking = true;
        component.isThinking = false;
        try {
            const response = await component.api.back.post('audio.text-to-speech', {
                text,
                voiceName: component.selectedVoice.name,
                languageCode: component.language || 'en-US',
                optimize: true
            });
            if (response.success && response.file) {
                await this.playAzureAudio(component, response.file);
            }
            else {
                console.error('Azure TTS failed:', response.error);
                component.errorMessage = 'Speech synthesis failed';
            }
        }
        catch (error) {
            console.error('Azure TTS error:', error);
            component.errorMessage = 'Speech synthesis failed';
        }
        finally {
            component.isSpeaking = false;
        }
    }
    /**
     * Play Azure audio from base64 data
     */
    static async playAzureAudio(component, base64Audio) {
        try {
            // Convert base64 to blob
            const audioData = atob(base64Audio);
            const arrayBuffer = new ArrayBuffer(audioData.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            for (let i = 0; i < audioData.length; i++) {
                uint8Array[i] = audioData.charCodeAt(i);
            }
            const audioBlob = new Blob([uint8Array], { type: 'audio/mpeg' });
            const audioUrl = URL.createObjectURL(audioBlob);
            // Create audio element
            const audio = new Audio(audioUrl);
            audio.volume = component.volume;
            // Setup audio analysis for equalizer
            audio_analysis_helper_1.AudioAnalysisHelper.setupAudioElementAnalysis(component, audio);
            return new Promise((resolve, reject) => {
                audio.addEventListener('ended', () => {
                    URL.revokeObjectURL(audioUrl);
                    resolve();
                });
                audio.addEventListener('error', (error) => {
                    URL.revokeObjectURL(audioUrl);
                    reject(error);
                });
                audio.play().catch(reject);
            });
        }
        catch (error) {
            console.error('Audio playback failed:', error);
            throw error;
        }
    }
    /**
     * Get recommended Azure voices for the current language
     */
    static async getRecommendedAzureVoices(component) {
        try {
            const response = await component.api.back.get('audio.recommended-voices');
            if (response.success && response.voices) {
                component.availableVoices = response.voices;
            }
        }
        catch (error) {
            console.error('Failed to load recommended Azure voices:', error);
        }
    }
    /**
     * Get Azure voices by gender
     */
    static async getAzureVoicesByGender(component, gender) {
        try {
            const response = await component.api.back.get('audio.azure-voices-by-gender', {
                queryParams: {
                    gender,
                    locale: component.language || 'en-US'
                }
            });
            if (response.success && response.voices) {
                component.availableVoices = response.voices;
            }
        }
        catch (error) {
            console.error(`Failed to load Azure ${gender} voices:`, error);
        }
    }
    /**
     * Get Azure neural voices for the current language
     */
    static async getNeuralAzureVoices(component) {
        try {
            const response = await component.api.back.get('audio.azure-neural-voices', {
                queryParams: {
                    locale: component.language || 'en-US'
                }
            });
            if (response.success && response.voices) {
                component.availableVoices = response.voices;
            }
        }
        catch (error) {
            console.error('Failed to load Azure neural voices:', error);
        }
    }
    /**
     * Handle voice selection change and update component state
     */
    static handleVoiceChange(component) {
        // Voice change will be effective on next conversation start
        // as we pass the current voiceId value when starting the conversation
        // Update selected voice for UI consistency - adapted for Azure
        if (component.availableVoices && component.voice) {
            component.selectedVoice = component.availableVoices.find(voice => voice.name === component.voice);
        }
    }
    /**
     * Refresh voices for current language
     */
    static refreshVoicesForLanguage(component) {
        const currentLang = component.language;
        if (currentLang) {
            // Reload voices for new language
            this.loadAzureVoices(component);
        }
    }
}
exports.AzureVoicesHelper = AzureVoicesHelper;
